/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComboChartProps, LineAreaItem } from './ComboChart.types';
import {
  findNearestDataPoint,
  getStartAndEndAxesOffset
} from '#utils/PRIVATE_chartUtils/layoutUtils';
import { AreaSeries } from '#PRIVATE_LineAreaComponents/AreaSeries';
import { getMarkers, getSeriesData } from '#utils/PRIVATE_chartUtils/utils';
import { MarkersWithLabel } from '#PRIVATE_Chart/MarkersWithLabel';
import { getLineAreaChartNavUtil } from '#utils/PRIVATE_chartUtils/navUtils';
import { Chart } from '#PRIVATE_Chart';
import { ChartSeriesData, Scale, SizeHandle, TimeAxisProps } from '#utils/UNSAFE_visTypes/chart';
import { useUser } from '#hooks/UNSAFE_useUser';
import { getColorRamp } from '#utils/UNSAFE_visUtils';
import { LineSeries } from '#PRIVATE_LineAreaComponents/LineSeries';
import { Point } from '#utils/PRIVATE_visSVGUtils';
import { getLineAreaSegments } from '#utils/PRIVATE_chartUtils/utils';
import { colorSchemeVars } from '@oracle/oraclejet-internal-theme-contract/themeContract.css';
import { getItemColor, getMarkerType } from '#utils/PRIVATE_chartUtils/dataStyleUtils';
import { BarGroup } from '#UNSAFE_BarChart/BarGroup';
import { getBarPosition } from '#UNSAFE_BarChart/utils/barLayoutUtils';
import { getLineAreaChartItemPosition } from '#PRIVATE_LineAreaComponents/utils/chartUtils';
import { getBarGapRatio } from '#UNSAFE_BarChart/utils/barSeriesUtils';
import { Ref, VNode } from 'preact';
import { forwardRef } from 'preact/compat';

const LINE_WITH_AREA_OPACITY = 0.2;

export type ForwardRefComboChart = <K extends string | number, D extends LineAreaItem<K>>(
  props: ComboChartProps<K, D> & {
    ref?: Ref<SizeHandle>;
  }
) => VNode | null;

/**
 * The ComboChart displays information graphically using bars, lines and filled areas, making relationships among the data easier to understand.
 */
export const ComboChart = forwardRef(
  <K extends string | number, D extends LineAreaItem<K>>(
    { width,
      height,
      selectedIds = [],
      hiddenIds = [],
      highlightedIds,
      hideAndShowBehavior = 'none',
      orientation = 'vertical',
      yAxis,
      series,
      groups,
      getDataItem,
      selectionMode = 'none',
      drilling = 'off',
      dataItemGaps = 0.5,
      dataLabelEffect = 'outline',
      stacking = 'off',
      ...props
    }: ComboChartProps<K, D>,
    ref: Ref<SizeHandle>
  ) => {
    const { direction, forcedColors } = useUser();
    const isHighContrast = forcedColors === 'active';
    const filteredBarSeries = series.filter((currentSeries) => currentSeries.type === 'bar');
    const isRtl = direction === 'rtl';
    const isLog = yAxis?.scale === 'log';
    const timeAxisType = (props.xAxis as TimeAxisProps)?.timeAxisType;
    const hiddenSet = new Set(hiddenIds);
    const selectedSet = new Set(selectedIds);
    const highlightedSet = new Set(highlightedIds);
    const isHoriz = orientation === 'horizontal';
    const colors = getColorRamp();
    // TODO: JET-71332 ; should only be (number | undefined)[][]
    // remove the type inference from scatter chart item.
    let renderedSeries = getSeriesData<K, D>(
      series.length,
      groups.length,
      getDataItem,
      hiddenSet,
      isLog
    );

    const isStacked = stacking != 'off';
    const stackLabelStyle = typeof stacking === 'string' ? undefined : stacking.stackLabelStyle;
    const getStackGroup =
      (stacking === 'on'
        ? (s: ChartSeriesData<K, D>) => s.id as string
        : stacking != 'off'
          ? stacking.getStackGroup
          : undefined) || (() => 'default');
    const getStackLabel =
      typeof stacking === 'object' && stacking.getStackLabel ? stacking.getStackLabel : undefined;

    const renderedGetDataItem = (seriesIndex: number, groupIndex: number): D | undefined => {
      if (!isStacked) return getDataItem(seriesIndex, groupIndex);
      return {
        ...getDataItem(seriesIndex, groupIndex),
        value: renderedSeries[seriesIndex][groupIndex]
      } as D;
    };

    const getGapRatio = () => {
      return 1;
    };

    const getNextChartItemFunc = (startIndex: number, endIndex: number) => {
      // TODO: JET-71332
      //@ts-ignore
      return getLineAreaChartNavUtil<D>(
        getDataItem,
        series.length,
        startIndex,
        endIndex,
        isStacked,
        isRtl,
        isHoriz
      );
    };
    const isSplitDualY = typeof props.y2Axis?.split === 'number';
    const getDataItemPos = (
      xScale: Scale,
      yScale: Scale,
      y2Scale: Scale,
      axisStepWidth: number,
      averageGroupZ: number,
      gapRatio: number
    ) => {
      const _getBarPosition = hasBar
        ? getBarPosition(
          filteredBarSeries,
          groups,
          getDataItem,
          xScale,
          yScale,
          y2Scale,
          hiddenSet,
          dataItemGaps,
          axisStepWidth,
          averageGroupZ,
          gapRatio,
          yAxis?.scale === 'log',
          isStacked,
          isRtl,
          isHoriz,
          isSplitDualY,
          getStackGroup,
          timeAxisType
        )
        : undefined;

      return (seriesIndex: number, groupIndex: number) => {
        const seriesItem = series[seriesIndex];
        if (seriesItem.type === 'bar') {
          return _getBarPosition ? _getBarPosition(seriesIndex, groupIndex) : undefined;
        } else {
          return getLineAreaChartItemPosition(
            seriesIndex,
            groupIndex,
            renderedSeries,
            getDataItem,
            groups,
            isHoriz,
            xScale,
            yScale,
            timeAxisType
          );
        }
      };
    };

    const getMarkerColorAndType = (seriesIndex: number, groupIndex: number) => {
      const color = getItemColor(seriesIndex, groupIndex, series, getDataItem);
      const markerType = getMarkerType(seriesIndex, groupIndex, getDataItem);
      return { color, markerType };
    };

    const findNearest = (xScale: Scale, yScale: Scale, y2Scale: Scale) => {
      const getSeriesDataInfo = () => {
        return {
          offset: 0,
          dataWidth: 0
        };
      };

      const getYCoord = (value: number, yScale: Scale) => {
        return yScale.transform(value);
      };

      return findNearestDataPoint<K, D>(
        series,
        groups,
        xScale,
        yScale,
        renderedGetDataItem,
        isStacked,
        getSeriesDataInfo,
        getYCoord,
        isLog,
        0,
        hiddenSet,
        timeAxisType,
        y2Scale
      );
    };
    const markers = getMarkers();

    const allLineSegmentCoords: Point[][] = [];

    const { hasBar, hasCenteredSeries, hasUncenteredSeries, hasArea } = (
      series as { lineType: string; type: string }[]
    ).reduce(
      (acc, s) => {
        return {
          hasArea: acc.hasArea || s.type === 'area' || s.type === 'lineWithArea',
          hasBar: acc.hasBar || s.type === 'bar',
          hasCenteredSeries:
            acc.hasCenteredSeries ||
            s.lineType === 'centeredSegmented' ||
            s.lineType === 'centeredStepped',
          hasUncenteredSeries:
            acc.hasUncenteredSeries || s.lineType === 'segmented' || s.lineType === 'stepped'
        };
      },
      { hasBar: false, hasCenteredSeries: false, hasUncenteredSeries: false, hasArea: false }
    );

    const getOffset = (groupCount: number) => {
      return getStartAndEndAxesOffset(
        hasCenteredSeries,
        hasUncenteredSeries,
        isHoriz,
        groupCount,
        hasBar
      );
    };

    return (
      <Chart
        ref={ref}
        width={width}
        height={height}
        selectedIds={selectedSet}
        hiddenIds={hiddenIds}
        hideAndShowBehavior={hideAndShowBehavior}
        orientation={orientation}
        yAxis={yAxis}
        series={series}
        defaultOverviewContentRenderer={() => {
          return <OverviewComboChart groups={groups} series={series} getDataItem={getDataItem} />;
        }}
        groups={groups}
        selectionMode={selectionMode}
        drilling={drilling}
        isStacked={isStacked}
        getDataItem={getDataItem}
        findNearest={findNearest}
        getDataItemPos={getDataItemPos}
        getMarkerInfo={getMarkerColorAndType}
        renderGridLinesInFront={hasArea}
        getNavUtil={getNextChartItemFunc}
        getGapRatio={getGapRatio}
        getStartAndEndAxesOffset={getOffset}
        isRtl={isRtl}
        stackLabelStyle={stackLabelStyle}
        getStackGroup={getStackGroup}
        {...props}>
        {({
          xStartIndex,
          xEndIndex,
          xScale,
          groupsInfo,
          yScale,
          y2Scale,
          activeId,
          focusedItemInfo,
          hoveredItemInfo,
          isPointInsideMarquee,
          getTextDimensions,
          defaultFontSize,
          resolvedColors,
          axisStepWidth,
          averageGroupZ,
          getItemAriaLabel,
          plotAreaClipPathId
        }) => {
          let barSeriesIndex = -1;
          const leafGroups = groupsInfo.map(({ item }) => item);
          renderedSeries = getSeriesData<K, D>(
            series.length,
            leafGroups.length,
            getDataItem,
            hiddenSet,
            isLog
          );

          return (
            <>
              {renderedSeries.map((valueSeries, index) => {
                const currentSeries = series[index];
                const isAssociatedToY2 = currentSeries.associatedYAxis === 'y2';
                const areaOpacity =
                  currentSeries.type === 'lineWithArea' ? LINE_WITH_AREA_OPACITY : 1;
                const isAreaRendered =
                  currentSeries.type === 'area' || currentSeries.type === 'lineWithArea';
                const isContrastLineNeeded = currentSeries.type === 'area';

                if (valueSeries.length === 0) {
                  return;
                }

                const lineSegmentCoords: Point[][] = getLineAreaSegments<K, D>(
                  isAssociatedToY2 ? y2Scale : yScale,
                  xScale,
                  valueSeries,
                  xStartIndex,
                  xEndIndex,
                  isLog,
                  leafGroups,
                  index,
                  renderedGetDataItem,
                  timeAxisType
                );
                allLineSegmentCoords[index] = lineSegmentCoords[0];
                const isBottomSegmentCurved =
                  currentSeries.type !== 'bar'
                    ? currentSeries.lineType === 'curved' &&
                    isStacked &&
                    renderedSeries.length > 1 &&
                    index > 0
                    : isStacked && renderedSeries.length > 1 && index > 0;

                const isHighlighted =
                  highlightedIds === undefined ||
                  highlightedSet.size === 0 ||
                  highlightedSet.has(currentSeries.id);

                const prevCoords =
                  isStacked && index != 0
                    ? allLineSegmentCoords.reduce((prevValue, newValue, j) => {
                      // find the index i such that i < index, and series i  has at least
                      // one or more data item unhidden. Only such series will can be
                      // used as bottomCoord for current series. Otherwise we have to use
                      // baseline coord
                      if (j >= index) {
                        return prevValue;
                      }
                      if (newValue.length > 0) {
                        return j;
                      }
                      return prevValue;
                    }, 0)
                    : undefined;
                if (currentSeries.type === 'bar') barSeriesIndex = barSeriesIndex + 1;
                return (
                  <>
                    {currentSeries.type === 'bar' &&
                      leafGroups.slice(xStartIndex, xEndIndex + 1).map((_, index) => {
                        const barGroup = (
                          <BarGroup
                            plotAreaClipPathId={plotAreaClipPathId}
                            yScale={yScale}
                            y2Scale={y2Scale}
                            xScale={xScale}
                            splitDualY={isSplitDualY}
                            groupIndex={index + xStartIndex}
                            selectedIds={selectedSet}
                            hiddenIds={hiddenSet}
                            highlightedIds={highlightedIds ? highlightedSet : highlightedIds}
                            isDrillEnabled={drilling === 'on'}
                            series={filteredBarSeries}
                            barGapRatio={getBarGapRatio(isStacked, xStartIndex, xEndIndex)}
                            dataItemGaps={dataItemGaps}
                            isPointInsideMarquee={isPointInsideMarquee}
                            isStacked={isStacked}
                            axisStepWidth={axisStepWidth}
                            averageGroupZ={averageGroupZ!}
                            group={leafGroups[index]}
                            timeAxisType={(props.xAxis as TimeAxisProps)?.timeAxisType}
                            colors={resolvedColors}
                            activeId={activeId}
                            isLog={yAxis?.scale === 'log'}
                            orientation={orientation}
                            getDataItem={getDataItem}
                            isSelectionEnabled={selectionMode != 'none'}
                            isRtl={isRtl}
                            focusedItemInfo={focusedItemInfo}
                            hoveredItemInfo={hoveredItemInfo}
                            getTextDimensions={getTextDimensions}
                            defaultFontSize={defaultFontSize}
                            seriesIndex={barSeriesIndex}
                            getItemAriaLabel={getItemAriaLabel}
                            getStackLabel={getStackLabel}
                          />
                        );

                        return barGroup;
                      })}
                    {isAreaRendered && (
                      <AreaSeries
                        yScale={isAssociatedToY2 ? y2Scale : yScale}
                        plotAreaClipPathId={plotAreaClipPathId}
                        lineType={currentSeries.lineType}
                        color={currentSeries.areaColor || colors[index % colors.length]}
                        isHorizontal={isHoriz}
                        areaColorOpacity={areaOpacity}
                        isBottomSegmentCurved={isBottomSegmentCurved}
                        bottomCoords={
                          prevCoords !== undefined &&
                            allLineSegmentCoords[prevCoords].some((value) => value != null)
                            ? allLineSegmentCoords[prevCoords]
                            : undefined
                        }
                        lineSegments={lineSegmentCoords}
                        isLog={isLog}
                        isHighlighted={isHighlighted}
                        seriesIndex={index}
                        groupWidth={axisStepWidth}
                        isRtl={isRtl}
                        timeAxisType={timeAxisType}
                      />
                    )}
                    {currentSeries.type === 'line' && (
                      <LineSeries
                        plotAreaClipPathId={plotAreaClipPathId}
                        lineType={currentSeries.lineType}
                        lineColor={
                          isContrastLineNeeded
                            ? colorSchemeVars.dvt.contrastLine
                            : currentSeries.lineColor
                        }
                        lineWidth={isContrastLineNeeded ? 1.25 : currentSeries.lineWidth}
                        lineStyle={currentSeries.lineStyle || 'solid'}
                        color={currentSeries.lineColor || colors[index % colors.length]}
                        lineSegmentCoords={lineSegmentCoords}
                        isHorizontal={isHoriz}
                        isHighlighted={isHighlighted}
                        seriesIndex={index}
                        groupWidth={axisStepWidth}
                        isRtl={isRtl}
                        timeAxisType={timeAxisType}
                      />
                    )}
                    {
                      /*for non stacked charts, markers should render in the order of series.
                       some markers might get hidden behind area but this preserves the legacy behavior. */
                      !isStacked && (currentSeries.type === 'line' || isAreaRendered) && (
                        <MarkersWithLabel
                          yScale={isAssociatedToY2 ? y2Scale : yScale}
                          xScale={xScale}
                          groups={leafGroups}
                          series={currentSeries}
                          timeAxisType={timeAxisType}
                          color={
                            colors[
                            index % colors.length
                            ] /* TODO : make this use getItemColor function from dataStyleUtils. */
                          }
                          seriesIndex={index}
                          startIndex={xStartIndex}
                          endIndex={xEndIndex}
                          markerType={markers[index % markers.length]}
                          orientation={orientation}
                          isLog={isLog}
                          activeId={activeId}
                          getDataItem={getDataItem}
                          isPointInsideMarquee={isPointInsideMarquee}
                          selectedIds={selectedSet}
                          focusedItemIndex={
                            focusedItemInfo.seriesIndex === index &&
                              focusedItemInfo.isFocusVisible &&
                              !(props.isDataCursorEnabled && props.dataCursorStyle?.isMarkerDisplayed)
                              ? focusedItemInfo.groupIndex
                              : undefined
                          }
                          hoveredItemIndex={
                            hoveredItemInfo?.isCurrent && hoveredItemInfo.seriesIndex === index
                              ? hoveredItemInfo.groupIndex
                              : undefined
                          }
                          isSelectionEnabled={
                            selectionMode === 'single' || selectionMode === 'multiple'
                          }
                          isDrillEnabled={drilling === 'on'}
                          defaultFontSize={defaultFontSize}
                          dataLabelEffect={dataLabelEffect}
                          isHighContrast={isHighContrast}
                          getItemAriaLabel={getItemAriaLabel}
                          isRtl={isRtl}
                        />
                      )
                    }
                  </>
                );
              })}
            </>
            //TODO - handle isStack part
          );
        }}
      </Chart>
    );
  }) as ForwardRefComboChart

function OverviewComboChart<K extends string | number, D extends LineAreaItem<K>>(
  props: ComboChartProps<K, D>
) {
  return (
    <ComboChart
      datatip={() => ({ content: '' })}
      zoomAndScroll="off"
      plotArea={{
        yMajorTick: {
          isRendered: false
        },
        xMajorTick: {}
      }}
      xAxis={{
        tickLabel: {
          autoRotate: false,
          // @ts-ignore
          isRendered: props.xAxis?.timeAxisType ? true : false
        }
      }}
      series={props.series}
      groups={props.groups}
      getDataItem={props.getDataItem}
      yAxis={{
        tickLabel: {
          isRendered: false
        }
      }}
      overview="off"
    />
  );
}
